import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  FC,
  ReactNode,
} from 'react';

import {
  ScheduleContextType,
  ScheduleAction,
  TSchedule,
} from '../@types/calendar';
import { UserContext } from './userContext';

const iState: TSchedule = {
  attendance: [],
  admissions: [],
  unrepliedRsvpCount: 0,
  invitationCount: 0,
};
export const ScheduleContext = createContext<ScheduleContextType>({
  scheduleState: iState,
  dispatch: () => null,
  setAttendance: () => null,
  setAdmissions: () => null,
  setUnrepliedRsvpCount: () => null,
  setInvitationCount: () => null,
});

export const userReducer = (
  scheduleState: TSchedule,
  action: ScheduleAction,
): TSchedule => {
  switch (action.type) {
    case 'SET_MY_ATTENDANCE':
      return {
        ...scheduleState,
        attendance: action.payload,
      };
    case 'SET_MY_ADMISSIONS':
      return {
        ...scheduleState,
        admissions: action.payload,
      };
    case 'SET_MY_UNREPLIED_RSVP_COUNT':
      return {
        ...scheduleState,
        unrepliedRsvpCount: action.payload,
      };
    case 'SET_MY_INVITATION_COUNT':
      return {
        ...scheduleState,
        invitationCount: action.payload,
      };
    case 'RESET_SCHEDULE':
      return {
        ...iState,
      };
    default:
      return scheduleState;
  }
};

const ScheduleProvider: FC<{
  children: ReactNode;
  onSetAttendance?: (payload: TSchedule['attendance']) => void;
  onSetAdmissions?: (payload: TSchedule['admissions']) => void;
  onSetUnrepliedRsvpCount?: (payload: number) => void;
  onSetInvitationCount?: (payload: number) => void;
}> = ({
  children,
  onSetAttendance,
  onSetAdmissions,
  onSetUnrepliedRsvpCount,
  onSetInvitationCount,
}) => {
  const localState: TSchedule = iState;
  const initialState = useContext(ScheduleContext);
  const [scheduleState, dispatch] = useReducer(
    userReducer,
    localState || initialState,
  );

  // ScheduleProvider is mounted once for the whole tab (gatsby-browser.tsx),
  // and attendance/admissions/RSVP data is only repopulated lazily by
  // whichever calendar/schedule component happens to mount. Reset on any
  // false->true login transition so a new user doesn't see the previous
  // user's schedule data until their own query refetches.
  const { state: userState } = useContext(UserContext);
  const prevIsLoggedIn = useRef(userState.isLoggedIn);
  useEffect(() => {
    if (!prevIsLoggedIn.current && userState.isLoggedIn) {
      dispatch({ type: 'RESET_SCHEDULE' });
    }
    prevIsLoggedIn.current = userState.isLoggedIn;
  }, [userState.isLoggedIn]);

  const setAdmissions = (admissions: TSchedule['admissions'] | undefined) => {
    dispatch({ type: 'SET_MY_ADMISSIONS', payload: admissions });
    onSetAdmissions && onSetAdmissions(admissions);
  };
  const setAttendance = (attendance: TSchedule['attendance'] | undefined) => {
    dispatch({ type: 'SET_MY_ATTENDANCE', payload: attendance });
    onSetAttendance && onSetAttendance(attendance);
  };
  const setUnrepliedRsvpCount = (count: number) => {
    dispatch({ type: 'SET_MY_UNREPLIED_RSVP_COUNT', payload: count });
    onSetUnrepliedRsvpCount && onSetUnrepliedRsvpCount(count);
  };
  const setInvitationCount = (count: number) => {
    dispatch({ type: 'SET_MY_INVITATION_COUNT', payload: count });
    onSetInvitationCount && onSetInvitationCount(count);
  };
  return (
    <ScheduleContext.Provider
      value={{
        scheduleState,
        dispatch,
        setAdmissions,
        setAttendance,
        setUnrepliedRsvpCount,
        setInvitationCount,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

export default ScheduleProvider;
